<template>
    <van-dialog v-model="progress.show" :show-confirm-button="false">
        <div class="progress">
            <div class="title">{{ progress.title }}</div>
            <div class="loading">
                <div class="name">
                    <span>上传进度：</span>
                    <span>{{ progressLoadedComputed }}MB/</span>
                    <span>{{ progressTotalComputed }}MB</span>
                </div>
                <div class="bars">
                    <div class="active" :style="{ width: `${progressPercentageComputed}%` }"></div>
                </div>
            </div>
            <div class="tip">文件上传中，请勿离开当前页面...</div>
            <div class="footer">
                <div class="link" @click="onCancel">取消上传</div>
            </div>
        </div>
    </van-dialog>
</template>

<script>
import api from '@/utils/request'

export default {
    props: {
        value: {
            type: String,
            default: ''
        }
    },
    data() {
        return {
            abortController: null,
            progress: {
                show: false,
                loaded: 0,
                total: 0,
                title: ''
            }
        }
    },
    computed: {
        progressLoadedComputed() {
            return Number((this.progress.loaded / 1024 / 1024).toFixed(2))
        },
        progressTotalComputed() {
            return Number((this.progress.total / 1024 / 1024).toFixed(2))
        },
        progressPercentageComputed() {
            return this.progress.total ? Math.round(this.progress.loaded / this.progress.total * 100) : 0
        }
    },
    methods: {
        async init(chooseConfig = {}, uploadConfig = {}) {
            try {
                const files = await this.onChoose(chooseConfig)
                this.progress.show = true
                const res = []
                for (let i = 0; i < files.length; i++) {
                    this.progress.loaded = 0
                    this.progress.total = 0
                    this.progress.title = files[i].file.name
                    const url = await this.onUpload(files[i].file, uploadConfig)
                    res.push(url)
                }
                this.$toast('上传成功')
                this.$emit('input', res.join(','))
                this.$emit('success', res.join(','))
            } catch (error) {
                if(error.errorMsg) this.$toast(error.errorMsg)
            }
            this.progress.show = false
        },
        // 选择
        async onChoose(options = {}) {
            const { multiple = false, accept = '.zip,.rar', limit = 1, fileSize = 500, dropzone } = options
            // 创建文件选择元素
            const input = document.createElement('input')
            input.setAttribute('type', 'file')
            input.setAttribute('style', 'display:none')
            input.setAttribute('accept', accept)
            if (multiple) input.setAttribute('multiple', 'multiple')
            if (dropzone) input.setAttribute('dropzone', dropzone)
            // 将文件选择元素添加到文档中
            document.body.appendChild(input)

            // 触发文件选择元素的点击事件
            input.click()

            // 等待文件选择元素的change事件
            return new Promise((resolve, reject) => {
                input.addEventListener('change', (event) => {
                    const target = event.target
                    const list = Object.values(target.files) || []

                    // 从文档中移除文件选择元素
                    document.body.removeChild(input)

                    // 判断是否超出上传数量限制
                    if (list.length > limit) {
                        reject({ errorType: 'limit', errorMsg: `文件数量已超【${limit}】`, files: target.files })
                        return
                    }

                    // 判断是否超出上传文件大小限制
                    if (list.some((item) => item.size / 1024 ** 2 > fileSize)) {
                        reject({ errorType: 'fileSize', errorMsg: `文件大小已超【${fileSize}Mb】`, files: target.files })
                        return
                    }
                    // 抛出附件，并生成附件uid
                    resolve(list.map((file, index) => ({ file, uid: Date.now() + index })))
                })
                input.addEventListener('cancel', () => {
                    reject({ errorType: 'cancel' })
                })
            })
        },
        // 上传
        async onUpload(file, options = {}) {
            const {
                module = 'efficacy',
                category = 'attach',
                async = '',
                urlMode = 'absolute',
                ...rest
            } = options
            const formData = new FormData()
            formData.append('file', file, file.name)
            formData.append('module', module)
            formData.append('category', category)
            if (async) formData.append('async', async)
            for (let k in rest) {
                formData.append(k, rest[k])
            }
            this.abortController = new AbortController
            const { data } = await api({
                method: 'post',
                url: `/file-service/file/uploadOther`,
                data: formData,
                headers: {
                    'Content-Type': 'multipart/form-data'
                },
                signal: this.abortController.signal,
                onUploadProgress: (progressEvent) => {
                    const { loaded, total } = progressEvent;
                    this.progress.loaded = loaded
                    this.progress.total = total
                },
                withCredentials: false,
                timeout: 0
            })
            if (urlMode === 'relative') {
                return data.relativeUrl
            } else {
                return data.absoluteUrl
            }
        },
        // 取消
        async onCancel() {
            try {
                await this.$dialog.confirm({
                    title: '是否确认取消上传?',
                    confirmButtonText: '确认',
                    cancelButtonText: '取消',
                    closeOnClickOverlay: false
                })
                this.abortController && this.abortController.abort()
                this.progress.show = false
            } catch (error) { }
        }
    }
}
</script>

<style scoped lang="scss">
.progress {
    padding: 50px 20px;

    .title {
        text-align: center;
        font-weight: bold;
        color: #4187f7;
        word-break: break-all;
    }

    .loading {
        background: #f2f2f2;
        padding: 20px 15px;
        border-radius: 4px;
        margin-top: 25px;

        .name {
            font-size: 16px;
        }

        .bars {
            position: relative;
            height: 15px;
            background: #e4e4e4;
            border-radius: 50px;
            overflow: hidden;
            margin-top: 10px;

            .active {
                position: absolute;
                left: 0;
                top: 0;
                bottom: 0;
                width: 10%;
                background: #38ab55;
                transition: .3s;
            }
        }
    }

    .tip {
        margin-top: 40px;
        font-size: 16px;
        text-align: center;
        color: #f6752c;
    }

    .footer {
        display: flex;
        align-items: center;
        justify-content: center;
        margin-top: 50px;

        .link {
            cursor: pointer;
            color: #f00;
            font-size: 16px;

            &:hover {
                opacity: 0.7;
            }
        }
    }
}
</style>